import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Phone, Mail, Save, UserPlus, Trash2, Inbox, DoorOpen, Copy, CheckCircle2, KeyRound, Building2, Globe, MapPin, RotateCcw, Filter, Pencil, FileText,
} from "lucide-react";
import { Checkbox } from "../ui/checkbox";

const CARE_OPTS = [
  ["long_sejour", "Long séjour"], ["court_sejour", "Court séjour"],
  ["convalescence", "Convalescence"], ["unite_protegee", "Unité protégée"],
];
const AVAIL_OPTS = [
  ["auto", "Automatique (selon chambres libres)"], ["quelques", "Quelques chambres disponibles"],
  ["limitee", "Disponibilité limitée"], ["attente", "Liste d'attente"], ["contact", "Contactez-nous"],
];
const REGION_OPTS = [
  ["", "—"], ["flandre", "Région flamande"], ["wallonie", "Région wallonne"], ["bruxelles", "Région de Bruxelles-Capitale"],
];

const TYPE_LABEL = {
  visite: "Demande de visite", message: "Message", appel: "Appel",
  info_famille: "Demande d'information — Famille",
  info_partenaire: "Demande d'information — Partenaire",
  compte_partenaire: "Demande d'accès partenaire",
};

export default function PartnerSpaceTab({ readOnly }) {
  const [settings, setSettings] = useState({ residence_name: "", contact_phone: "", contact_email: "", commune: "", region: "", website: "", phone: "", public_visible: true, availability_override: "auto", public_description: "", care_types: [] });
  const [accounts, setAccounts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [savingSet, setSavingSet] = useState(false);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ name: "", organization: "", email: "", language: "fr" });
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [fromRequestId, setFromRequestId] = useState(null);
  const [fResidence, setFResidence] = useState("all");
  const [fType, setFType] = useState("all");
  const [fPeriod, setFPeriod] = useState("all");
  const [selected, setSelected] = useState([]);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = async () => {
    try { const r = await api.get("/crm/email-templates"); setTemplates(r.data || []); } catch (e) { /* silencieux */ }
  };

  const load = async () => {
    try {
      const [s, a, r] = await Promise.all([
        api.get("/crm/settings"),
        api.get("/crm/partner-accounts"),
        api.get("/crm/partner-requests"),
      ]);
      setSettings(s.data); setAccounts(a.data || []); setRequests(r.data || []);
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const renameTemplate = async (tp) => {
    const label = window.prompt("Nouveau nom du modèle :", tp.label);
    if (!label || label === tp.label) return;
    try { await api.put(`/crm/email-templates/${tp.id}`, { kind: tp.kind, label, text: tp.text }); toast.success("Modèle renommé."); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const editTemplateText = async (tp) => {
    const text = window.prompt("Modifier le texte du modèle :", tp.text);
    if (text == null || text === tp.text) return;
    try { await api.put(`/crm/email-templates/${tp.id}`, { kind: tp.kind, label: tp.label, text }); toast.success("Modèle modifié."); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const deleteTemplate = async (tp) => {
    if (!window.confirm(`Supprimer le modèle « ${tp.label} » ? Cette action est irréversible.`)) return;
    try { await api.delete(`/crm/email-templates/${tp.id}`); toast.success("Modèle supprimé."); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    setSavingSet(true);
    try { await api.put("/crm/settings", settings); toast.success("Coordonnées enregistrées."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSavingSet(false);
  };

  const createAccount = async () => {
    if (!form.name.trim()) { toast.error("Indiquez le nom du contact partenaire."); return; }
    setCreating(true);
    try {
      const res = await api.post("/crm/partner-accounts", form);
      setCreated(res.data);
      setForm({ name: "", organization: "", email: "", language: "fr" });
      if (fromRequestId) {
        try { await api.put(`/crm/partner-requests/${fromRequestId}?status=traitee`); } catch (e) { /* non bloquant */ }
        setFromRequestId(null);
      }
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setCreating(false);
  };

  const openNewAccount = () => { setCreated(null); setFromRequestId(null); setForm({ name: "", organization: "", email: "", language: "fr" }); setDlg(true); };

  const openAccountFromRequest = (r) => {
    setCreated(null);
    setFromRequestId(r.id);
    setForm({ name: r.partner_name || "", organization: r.partner_org || "", email: r.contact_email || "", language: "fr" });
    setDlg(true);
  };

  const delAccount = async (id) => {
    if (!window.confirm("Supprimer ce compte partenaire ?")) return;
    try { await api.delete(`/crm/partner-accounts/${id}`); toast.success("Compte supprimé."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const markDone = async (id) => {
    try { await api.put(`/crm/partner-requests/${id}?status=traitee`); toast.success("Demande traitée."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const delRequest = async (id) => {
    if (!window.confirm("Mettre cette demande à la corbeille ? Elle sera récupérable 30 jours, puis effacée définitivement. La corbeille est partagée (CEO, directeurs, adjoints).")) return;
    try { await api.delete(`/crm/partner-requests/${id}`); toast.success("Demande envoyée à la corbeille."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Mettre ${selected.length} demande(s) traitée(s) à la corbeille ? (récupérables 30 jours)`)) return;
    try { const r = await api.post("/crm/partner-requests/bulk-delete", { ids: selected }); toast.success(`${r.data.moved || 0} demande(s) envoyée(s) à la corbeille.`); setSelected([]); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const clearTreated = async () => {
    if (!window.confirm("Envoyer TOUTES les demandes traitées à la corbeille ? (récupérables 30 jours)")) return;
    try { const r = await api.post("/crm/partner-requests/clear-treated"); toast.success(`${r.data.moved || 0} demande(s) envoyée(s) à la corbeille.`); setSelected([]); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const restoreReq = async (id) => {
    try { await api.put(`/crm/partner-requests/${id}/restore`); toast.success("Demande restaurée."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const permanentDelete = async (id) => {
    if (!window.confirm("Supprimer DÉFINITIVEMENT cette demande ? Cette action est irréversible.")) return;
    try { await api.delete(`/crm/partner-requests/${id}/permanent`); toast.success("Demande supprimée définitivement."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const emptyTrash = async () => {
    if (!window.confirm("Vider définitivement toute la corbeille ? Cette action est irréversible.")) return;
    try { const r = await api.post("/crm/partner-requests/empty-trash"); toast.success(`Corbeille vidée (${r.data.deleted || 0}).`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copié."); };
  const matchFilters = (r) => {
    if (fResidence !== "all" && (r.maison || "") !== fResidence) return false;
    if (fType !== "all") {
      const isFam = r.type === "info_famille";
      if (fType === "famille" && !isFam) return false;
      if (fType === "partenaire" && isFam) return false;
    }
    if (fPeriod !== "all" && r.created_at) {
      const d = new Date(r.created_at); const now = new Date();
      if (fPeriod === "7" || fPeriod === "30") {
        const days = fPeriod === "7" ? 7 : 30;
        if (d < new Date(now.getTime() - days * 86400000)) return false;
      } else if (fPeriod === "month") {
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      }
    }
    return true;
  };
  const newReqs = requests.filter((r) => r.status === "nouveau");
  const activeReqs = requests.filter((r) => r.status !== "traitee" && r.status !== "supprimee").filter(matchFilters);
  const treatedReqs = requests.filter((r) => r.status === "traitee").filter(matchFilters);
  const trashReqs = requests.filter((r) => r.status === "supprimee");
  const residenceOpts = [...new Set(requests.map((r) => r.maison).filter(Boolean))].sort();
  const treatedSelectable = treatedReqs.map((r) => r.id);
  const allTreatedSelected = treatedSelectable.length > 0 && treatedSelectable.every((id) => selected.includes(id));

  return (
    <div className="space-y-6" data-testid="partner-space-tab">
      {/* Coordonnées de contact */}
      <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02]">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><DoorOpen className="h-4 w-4 text-emerald-400" /> Coordonnées affichées aux partenaires</h3>
        <p className="text-sm text-muted-foreground mt-1">Ces informations sont visibles dans l'Espace Partenaires (bouton « Appeler » / « Écrire »).</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 items-end">
          <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> Nom de la résidence</Label><Input value={settings.residence_name || ""} onChange={(e) => setSettings({ ...settings, residence_name: e.target.value })} data-testid="ps-residence-name" /></div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> Téléphone</Label><Input value={settings.contact_phone || ""} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} placeholder="+32 2 ..." data-testid="ps-contact-phone" /></div>
          <div className="space-y-1.5"><Label className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> E-mail de contact</Label><Input value={settings.contact_email || ""} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} placeholder="contact@..." data-testid="ps-contact-email" /></div>
        </div>

        {/* Vitrine publique */}
        <div className="mt-5 rounded-xl border border-pink-500/20 bg-pink-500/[0.04] p-4" data-testid="ps-public-section">
          <h4 className="font-semibold flex items-center gap-2 text-pink-300"><Globe className="h-4 w-4" /> Vitrine publique (familles & partenaires)</h4>
          <p className="text-xs text-muted-foreground mt-1">Ces réglages contrôlent la page publique <code>/decouvrir</code>. Aucune donnée sensible n'y est jamais affichée.</p>
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <Checkbox checked={settings.public_visible !== false} onCheckedChange={(v) => setSettings({ ...settings, public_visible: !!v })} data-testid="ps-public-visible" />
            <span>Afficher cette résidence sur la vitrine publique</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5 h-5"><MapPin className="h-3.5 w-3.5" /> Commune</Label><Input value={settings.commune || ""} onChange={(e) => setSettings({ ...settings, commune: e.target.value })} placeholder="Ex : Anderlecht" data-testid="ps-commune" /></div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5 h-5">Région</Label>
              <select value={settings.region || ""} onChange={(e) => setSettings({ ...settings, region: e.target.value })} data-testid="ps-region" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                {REGION_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5 h-5"><Globe className="h-3.5 w-3.5" /> Site internet officiel</Label><Input value={settings.website || ""} onChange={(e) => setSettings({ ...settings, website: e.target.value })} placeholder="https://…" data-testid="ps-website" /></div>
            <div className="space-y-1.5"><Label className="flex items-center gap-1.5 h-5"><Phone className="h-3.5 w-3.5" /> Numéro de contact</Label><Input value={settings.phone || ""} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} placeholder="Ex : 02 000 00 00" data-testid="ps-public-phone" /></div>
          </div>
          <div className="space-y-1.5 mt-3">
            <Label>Disponibilité affichée</Label>
            <select value={settings.availability_override || "auto"} onChange={(e) => setSettings({ ...settings, availability_override: e.target.value })} data-testid="ps-availability" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
              {AVAIL_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="mt-3">
            <Label>Prises en charge proposées</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              {CARE_OPTS.map(([v, l]) => {
                const on = (settings.care_types || []).includes(v);
                return (
                  <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={on} onCheckedChange={(c) => {
                      const cur = new Set(settings.care_types || []);
                      c ? cur.add(v) : cur.delete(v);
                      setSettings({ ...settings, care_types: [...cur] });
                    }} data-testid={`ps-care-${v}`} />
                    <span>{l}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-4" data-testid="ps-reminder-section">
          <h4 className="font-semibold flex items-center gap-2 text-violet-300"><CheckCircle2 className="h-4 w-4" /> Rappel accusé de réception (direction)</h4>
          <p className="text-xs text-muted-foreground mt-1">Si la direction n'a pas confirmé la réception d'une visite dans ce délai, un rappel lui est renvoyé automatiquement (une fois).</p>
          <div className="space-y-1.5 mt-3 max-w-xs">
            <Label>Délai avant rappel</Label>
            <select value={settings.reminder_delay_hours || 48} onChange={(e) => setSettings({ ...settings, reminder_delay_hours: parseInt(e.target.value, 10) })} data-testid="ps-reminder-delay" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
              <option value={24}>24 heures</option>
              <option value={48}>48 heures</option>
              <option value={72}>72 heures</option>
            </select>
          </div>
        </div>

        {!readOnly && <div className="mt-3"><Button onClick={saveSettings} disabled={savingSet} className="bg-emerald-600 hover:bg-emerald-500 gap-1.5" data-testid="ps-save-settings"><Save className="h-4 w-4" /> {savingSet ? "Enregistrement…" : "Enregistrer"}</Button></div>}
      </div>

      {/* Modèles d'e-mail */}
      <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02]" data-testid="ps-templates-section">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-sky-400" /> Modèles d'e-mail enregistrés</h3>
        <p className="text-sm text-muted-foreground mt-1">Réutilisez vos textes de recommandations (direction) et de relance. Créez-en depuis les dialogues « Prévenir la direction » et « Envoyer l'alerte par e-mail ».</p>
        {templates.length === 0 && <p className="text-sm text-muted-foreground py-3">Aucun modèle enregistré pour le moment.</p>}
        <div className="mt-3 space-y-2">
          {templates.map((tp) => (
            <div key={tp.id} className="flex items-start justify-between gap-3 rounded-xl border border-white/10 p-3 bg-white/[0.02]" data-testid={`template-row-${tp.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${tp.kind === "reco" ? "bg-emerald-500/15 text-emerald-300" : "bg-rose-500/15 text-rose-300"}`}>{tp.kind === "reco" ? "Recommandations" : "Relance"}</span>
                  <span className="font-semibold text-sm truncate">{tp.label}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{tp.text}</p>
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => renameTemplate(tp)} title="Renommer" data-testid={`template-rename-${tp.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => editTemplateText(tp)} title="Modifier le texte" data-testid={`template-edit-${tp.id}`}><FileText className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400 hover:text-rose-300" onClick={() => deleteTemplate(tp)} title="Supprimer" data-testid={`template-delete-${tp.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Demandes reçues */}
      <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02]">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Inbox className="h-4 w-4 text-orange-400" /> Demandes partenaires {newReqs.length > 0 && <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full">{newReqs.length} nouvelle(s)</span>}</h3>

        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="partner-requests-filters">
          <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><Filter className="h-3.5 w-3.5" /> Filtrer :</span>
          {residenceOpts.length > 1 && (
            <select value={fResidence} onChange={(e) => setFResidence(e.target.value)} data-testid="filter-residence" className="h-8 rounded-md border border-input bg-transparent px-2 text-xs">
              <option value="all">Toutes les résidences</option>
              {residenceOpts.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          )}
          <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="filter-type" className="h-8 rounded-md border border-input bg-transparent px-2 text-xs">
            <option value="all">Tous les types</option>
            <option value="famille">Famille</option>
            <option value="partenaire">Partenaire</option>
          </select>
          <select value={fPeriod} onChange={(e) => setFPeriod(e.target.value)} data-testid="filter-period" className="h-8 rounded-md border border-input bg-transparent px-2 text-xs">
            <option value="all">Toutes les dates</option>
            <option value="7">7 derniers jours</option>
            <option value="30">30 derniers jours</option>
            <option value="month">Ce mois-ci</option>
          </select>
          {(fResidence !== "all" || fType !== "all" || fPeriod !== "all") && (
            <button onClick={() => { setFResidence("all"); setFType("all"); setFPeriod("all"); }} className="text-xs text-sky-300 hover:text-sky-200" data-testid="filter-reset">Réinitialiser</button>
          )}
        </div>

        <div className="mt-3 space-y-2">
          {activeReqs.length === 0 && <p className="text-sm text-muted-foreground py-3">Aucune demande active pour le moment.</p>}
          {activeReqs.map((r) => (
            <div key={r.id} className={`rounded-xl border p-3 ${r.status === "nouveau" ? "border-orange-500/30 bg-orange-500/[0.05]" : "border-white/10"}`} data-testid="partner-request-row">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{r.partner_name}{r.partner_org ? ` · ${r.partner_org}` : ""}</p>
                  <p className="text-xs text-emerald-300">{TYPE_LABEL[r.type] || r.type}{r.room_label ? ` — ${r.room_label}` : ""}{r.wants_account ? " · 🔑 souhaite un accès" : ""}</p>
                  {(r.contact_phone || r.contact_email) && <p className="text-xs text-muted-foreground mt-0.5">{[r.contact_phone, r.contact_email].filter(Boolean).join(" · ")}</p>}
                  {r.message && <p className="text-sm text-foreground/80 mt-1">{r.message}</p>}
                  {r.prospect_id && <p className="text-[11px] text-sky-300 mt-1">Fiche prospect créée automatiquement dans le CRM.</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">{r.created_at ? new Date(r.created_at).toLocaleString("fr-BE") : ""}</p>
                </div>
                {!readOnly && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {(r.type === "compte_partenaire" || r.wants_account) && (
                      <Button size="sm" onClick={() => openAccountFromRequest(r)} className="gap-1.5 bg-emerald-600 hover:bg-emerald-500" data-testid="partner-request-create-access"><KeyRound className="h-4 w-4" /> Créer l'accès</Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => markDone(r.id)} className="gap-1.5" data-testid="partner-request-done"><CheckCircle2 className="h-4 w-4" /> Traiter</Button>
                    <Button size="sm" variant="outline" onClick={() => delRequest(r.id)} className="gap-1.5 text-red-400 hover:text-red-300 border-red-500/30 hover:bg-red-500/10" data-testid="partner-request-delete"><Trash2 className="h-4 w-4" /> Supprimer</Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {treatedReqs.length > 0 && (
          <details className="mt-4 group" data-testid="partner-treated-section" open>
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 select-none">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Demandes traitées ({treatedReqs.length})
            </summary>
            {!readOnly && (
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <Checkbox checked={allTreatedSelected} onCheckedChange={(v) => setSelected(v ? [...new Set([...selected, ...treatedSelectable])] : selected.filter((id) => !treatedSelectable.includes(id)))} data-testid="treated-select-all" />
                  <span>Tout sélectionner</span>
                </label>
                {selected.length > 0 && (
                  <Button size="sm" variant="outline" onClick={bulkDelete} className="gap-1.5 text-red-400 hover:text-red-300 border-red-500/30 hover:bg-red-500/10 h-7" data-testid="treated-bulk-delete"><Trash2 className="h-3.5 w-3.5" /> Corbeille ({selected.length})</Button>
                )}
                <Button size="sm" variant="outline" onClick={clearTreated} className="gap-1.5 text-red-400 hover:text-red-300 border-red-500/30 hover:bg-red-500/10 h-7 ml-auto" data-testid="treated-clear-all"><Trash2 className="h-3.5 w-3.5" /> Tout vider</Button>
              </div>
            )}
            <div className="mt-2 space-y-2">
              {treatedReqs.map((r) => (
                <div key={r.id} className="rounded-xl border border-white/10 p-3 opacity-80" data-testid="partner-request-treated">
                  <div className="flex items-start justify-between gap-3">
                    {!readOnly && <Checkbox className="mt-0.5 shrink-0" checked={selected.includes(r.id)} onCheckedChange={() => toggleSel(r.id)} data-testid="treated-checkbox" />}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm">{r.partner_name}{r.partner_org ? ` · ${r.partner_org}` : ""}</p>
                      <p className="text-xs text-emerald-300">{TYPE_LABEL[r.type] || r.type}{r.room_label ? ` — ${r.room_label}` : ""}</p>
                      {r.processed_at && <p className="text-[11px] text-muted-foreground mt-1">Traitée le {new Date(r.processed_at).toLocaleString("fr-BE")}{r.processed_by ? ` par ${r.processed_by}` : ""}</p>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Traitée</span>
                      {!readOnly && (
                        <Button size="icon" variant="ghost" onClick={() => delRequest(r.id)} className="text-red-400 hover:text-red-300 h-7 w-7" data-testid="partner-request-treated-delete"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}

        {trashReqs.length > 0 && (
          <details className="mt-4 group" data-testid="partner-trash-section" open>
            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 select-none">
              <Trash2 className="h-3.5 w-3.5 text-rose-400" /> Corbeille ({trashReqs.length}) <span className="text-[11px] opacity-70">— effacée automatiquement après 30 jours</span>
            </summary>
            {!readOnly && (
              <div className="flex mt-2">
                <Button size="sm" variant="outline" onClick={emptyTrash} className="gap-1.5 text-red-400 hover:text-red-300 border-red-500/30 hover:bg-red-500/10 h-7 ml-auto" data-testid="trash-empty"><Trash2 className="h-3.5 w-3.5" /> Vider la corbeille</Button>
              </div>
            )}
            <div className="mt-2 space-y-2">
              {trashReqs.map((r) => (
                <div key={r.id} className="rounded-xl border border-rose-500/20 bg-rose-500/[0.03] p-3" data-testid="partner-request-trash">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-sm">{r.partner_name}{r.partner_org ? ` · ${r.partner_org}` : ""}</p>
                      <p className="text-xs text-emerald-300">{TYPE_LABEL[r.type] || r.type}</p>
                      {r.deleted_at && <p className="text-[11px] text-muted-foreground mt-1">Supprimée le {new Date(r.deleted_at).toLocaleString("fr-BE")}{r.deleted_by ? ` par ${r.deleted_by}` : ""}</p>}
                    </div>
                    {!readOnly && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => restoreReq(r.id)} className="gap-1.5 h-7" data-testid="trash-restore"><RotateCcw className="h-3.5 w-3.5" /> Restaurer</Button>
                        <Button size="icon" variant="ghost" onClick={() => permanentDelete(r.id)} className="text-red-400 hover:text-red-300 h-7 w-7" data-testid="trash-permanent-delete"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </details>
        )}
      </div>

      {/* Comptes partenaires */}
      <div className="rounded-2xl border border-white/10 p-5 bg-white/[0.02]">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-sky-400" /> Comptes partenaires ({accounts.length})</h3>
          {!readOnly && <Button size="sm" onClick={openNewAccount} className="bg-emerald-600 hover:bg-emerald-500 gap-1.5" data-testid="ps-new-account"><UserPlus className="h-4 w-4" /> Créer un accès</Button>}
        </div>
        <div className="mt-3 space-y-2">
          {accounts.length === 0 && <p className="text-sm text-muted-foreground py-3">Aucun partenaire n'a encore d'accès. Créez-en un pour partager vos disponibilités.</p>}
          {accounts.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 p-3" data-testid="partner-account-row">
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{a.name}{a.organization ? ` · ${a.organization}` : ""}</p>
                <p className="text-xs text-muted-foreground">Identifiant : <span className="font-mono text-foreground/80">{a.username}</span>{a.email ? ` · ${a.email}` : ""}</p>
              </div>
              <Button size="icon" variant="ghost" onClick={() => delAccount(a.id)} data-testid="partner-account-delete" className="text-red-400 hover:text-red-300 shrink-0"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="partner-account-dialog">
          <DialogHeader><DialogTitle>Créer un accès partenaire</DialogTitle></DialogHeader>
          {!created ? (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Nom du contact *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Dr. Martin" data-testid="pa-name" /></div>
              <div className="space-y-1.5"><Label>Organisation</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Ex : Hôpital Brugmann, CPAS d'Anderlecht…" data-testid="pa-org" /></div>
              <div className="space-y-1.5"><Label>E-mail (facultatif)</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pa-email" /></div>
              <p className="text-xs text-muted-foreground">Un identifiant et un mot de passe temporaire seront générés. Le partenaire ne verra QUE les chambres disponibles, aucune donnée de résident.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/[0.06] p-4 space-y-2">
                <p className="text-sm font-semibold text-emerald-300">{created.email_sent ? `Accès créé ✅ — les identifiants ont été envoyés par e-mail à ${created.email}.` : "Accès créé ✅ — transmettez ces identifiants au partenaire :"}</p>
                <div className="flex items-center justify-between gap-2 text-sm"><span>Identifiant : <span className="font-mono font-bold">{created.username}</span></span><Button size="icon" variant="ghost" onClick={() => copy(created.username)}><Copy className="h-4 w-4" /></Button></div>
                <div className="flex items-center justify-between gap-2 text-sm"><span>Mot de passe : <span className="font-mono font-bold">{created.temp_password}</span></span><Button size="icon" variant="ghost" onClick={() => copy(created.temp_password)}><Copy className="h-4 w-4" /></Button></div>
                <p className="text-xs text-muted-foreground">Le partenaire devra changer ce mot de passe à sa première connexion.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            {!created ? (
              <>
                <Button variant="ghost" onClick={() => setDlg(false)}>Annuler</Button>
                <Button onClick={createAccount} disabled={creating} className="bg-emerald-600 hover:bg-emerald-500" data-testid="pa-submit">{creating ? "Création…" : "Créer l'accès"}</Button>
              </>
            ) : (
              <Button onClick={() => setDlg(false)} className="bg-emerald-600 hover:bg-emerald-500">Terminé</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
